import { HttpClient, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { IApiService_Delete, IApiService_Get, IApiService_Post, IApiService_Put } from "./api.model";
import { IAppResponse, IPagedResult } from "../../_models/network-response";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private hostUrl = environment.baseUrl;

  constructor(private http: HttpClient) { }

  get appId() {
    return String(environment.appId);
  }

  /**
   * Centralized helper to build and format all standard headers
   * @param isFormData - For method type GET set to true to skip setting the 
   */
  private createHeaders(authorization?: string, isFormData: boolean = false): { [key: string]: string } {
    const headerMap: Map<string, string> = new Map();

    if (authorization) {
      headerMap.set("Authorization", authorization);
    }
    // Note: browser will automatically set "multipart/form-data" along with the correct boundary token. Do not set manually
    if (!isFormData) {
      headerMap.set("Content-Type", "application/json");
    }
    
    // Static Header
    headerMap.set("X-ApplicationType", `Website`);
    headerMap.set("X-App-Name", this.appId);
    headerMap.set("X-App-Name-Id", environment.appName);
    // headerMap.set("X-Device-ID", "FingerprintJS"); TODO: Add this logic to get finger print
    headerMap.set("X-Timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);

    return Object.fromEntries(headerMap);
  }

  get<ResponseBody>(props: IApiService_Get): Observable<IAppResponse<ResponseBody>> {
    const url: string = `${props.BaseUrl ? props.BaseUrl : this.hostUrl}/${props.Url}`;
    const headers = this.createHeaders(props.Authorization, true);

    return this.http.get(url, { 
      params: props.Params,
      headers: headers
      })
      .pipe(map(response => response as IAppResponse<ResponseBody>));
  }

  getAllPageed<ResponseBody>(props: IApiService_Get): Observable<IAppResponse<IPagedResult<ResponseBody>>> {
    const url: string = `${props.BaseUrl ? props.BaseUrl : this.hostUrl}/${props.Url}`;
    const headers = this.createHeaders(props.Authorization, true);

    return this.http.get(url, { 
      params: props.Params, 
      headers: headers
     })
      .pipe(map(response => response as IAppResponse<IPagedResult<ResponseBody>>));
  }

  post<RequestBody, ResponseBody>(props: IApiService_Post<RequestBody>): Observable<IAppResponse<ResponseBody>> {
    const isFormData: boolean = props.Body instanceof FormData;
    const url: string = `${props.BaseUrl ? props.BaseUrl : this.hostUrl}/${props.Url}`;
    const headers = this.createHeaders(props.Authorization, isFormData);

    return this.http.post(url, props.Body, { 
      params: props.Params, 
      headers: headers
     }).pipe(map(response => response as IAppResponse<ResponseBody>));
  }

  put<RequestBody, ResponseBody>(props: IApiService_Put<RequestBody>): Observable<IAppResponse<ResponseBody>> {
    const isFormData: boolean = props.Body instanceof FormData;
    const url: string = `${props.BaseUrl ? props.BaseUrl : this.hostUrl}/${props.Url}`;
    const headers = this.createHeaders(props.Authorization, isFormData);

    return this.http.put(url, props.Body, { 
      params: props.Params, 
      headers: headers
     }).pipe(map(response => response as IAppResponse<ResponseBody>));
  }

  delete<RequestBody, ResponseBody>(props: IApiService_Delete<RequestBody>): Observable<IAppResponse<ResponseBody>> {
    const isFormData: boolean = props.Body instanceof FormData;
    const url: string = `${props.BaseUrl ? props.BaseUrl : this.hostUrl}/${props.Url}`;
    const headers = this.createHeaders(props.Authorization, isFormData);

    return this.http.delete(url, { 
      body: props.Body, 
      params: props.Params, 
      headers: headers
     }).pipe(map(response => response as IAppResponse<ResponseBody>));
  }

  deleteMany<T>(url: string, ids: string[]): Observable<T> {
    const headers = this.createHeaders();

    return this.http.request<T>("delete", `${this.hostUrl}/${url}`, { 
      body: { ids }, 
      headers: headers
    });
  }
}
